import type { CSSProperties } from 'react';
import { CalendarPlus, Stethoscope, FileText, Wrench } from 'lucide-react';

interface QuickActionsProps {
    onNewAppointment: () => void;
    onNewDiagnostic: () => void;
    onNewQuote: () => void;
    onNewJob: () => void;
}

/**
 * The four money-generating actions, promoted to the top of the dashboard.
 * These are the mouths of the funnel: Appointment → Diagnostic → Quote → Job.
 *
 * Layout: 2×2 on mobile, single row of 4 on sm+. Each tile is a full-height
 * tap target with the label always visible. Below lg the label sits under
 * the coin so it gets the tile's full width (styles/components.css,
 * .fd-forge-tile).
 *
 * Steel, the everyday-work metal (styles/theme.css, "Forged metals"): a
 * steel coin per tile and a slow steel ring with a gold glint round each.
 * These four are the only moving rings in the app. Each ring starts at a
 * different point of its turn, so the four never move in step.
 */
export const QuickActions = ({
    onNewAppointment,
    onNewDiagnostic,
    onNewQuote,
    onNewJob,
}: QuickActionsProps) => {
    const actions = [
        { label: 'New Appointment', icon: CalendarPlus, ring: '0s', onClick: onNewAppointment },
        { label: 'New Diagnostic', icon: Stethoscope, ring: '-2.4s', onClick: onNewDiagnostic },
        { label: 'New Quote', icon: FileText, ring: '-4.9s', onClick: onNewQuote },
        { label: 'New Job', icon: Wrench, ring: '-7.1s', onClick: onNewJob },
    ] as const;

    return (
        <nav aria-label="Quick actions" className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5 lg:mb-6">
            {actions.map(({ label, icon: Icon, ring, onClick }) => (
                <button
                    key={label}
                    type="button"
                    onClick={onClick}
                    className="fd-forge-tile"
                    style={{ '--fd-ring-delay': ring } as CSSProperties}
                >
                    <span aria-hidden="true" className="fd-coin">
                        <Icon strokeWidth={2} />
                    </span>
                    <span className="fd-forge-tile-label">{label}</span>
                </button>
            ))}
        </nav>
    );
};
